import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Button, Checkbox, ConfigProvider, Drawer, Radio, Spin, Switch, Tooltip } from 'antd';

import Bank1Icon from '@/assets/bank1.svg?react';
import Bank2Icon from '@/assets/bank2.svg?react';
import Bank3Icon from '@/assets/bank3.svg?react';
import type { PreOrderDetail } from '@/services/billingService';
import { getCurrencySymbol } from '@/utils/currency';

import styles from './PaymentDrawer.module.less';

interface PaymentDrawerProps {
  open: boolean;
  loading?: boolean;
  onClose: () => void;
  planData: PreOrderDetail | null;
  onPay: (autoRenew: boolean) => void;
}

export const PaymentDrawer: React.FC<PaymentDrawerProps> = ({
  open,
  loading = false,
  onClose,
  planData,
  onPay,
}) => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.resolvedLanguage === 'zh';
  const [autoRenew, setAutoRenew] = useState(true);
  const [agreed, setAgreed] = useState(false); // 默认未选中，需用户主动勾选协议
  const [paymentMethod, setPaymentMethod] = useState('bank_card'); // 默认选中银行卡支付

  // 每次打开抽屉时重置 agreed 为 false
  useEffect(() => {
    if (open) {
      setAgreed(false);
    }
  }, [open]);

  // 自动续费说明文案
  const autoRenewTooltip = (
    <div>
      <div style={{ marginBottom: 8 }}>{t('pricing.autoRenew.tooltip.line1')}</div>
      <div>{t('pricing.autoRenew.tooltip.line2')}</div>
    </div>
  );

  const payingRef = useRef(false);
  const handlePay = () => {
    if (!agreed) {
      // TODO: 显示提示
      return;
    }
    if (payingRef.current) return;
    payingRef.current = true;
    onPay(autoRenew);
    setTimeout(() => {
      payingRef.current = false;
    }, 1000);
  };

  // 从 productDetailList 中获取第一个产品的信息（通常只有一个产品）
  const firstProduct = planData?.productDetailList?.[0];
  const currencySymbol = getCurrencySymbol(firstProduct?.currencyUnit);

  // 调试信息：查看数据状态
  // console.log('PaymentDrawer 数据状态:', {
  //   loading,
  //   hasPlanData: !!planData,
  //   hasFirstProduct: !!firstProduct,
  //   planData,
  // });

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#111314', // 抽屉组件内的主题色改为黑色
          colorPrimaryHover: '#2d2f31', // hover 状态颜色
          colorPrimaryActive: '#000000', // active 状态颜色
          colorPrimaryBorder: '#111314', // 边框颜色
        },
      }}
    >
      <Drawer
        width={640}
        open={open}
        onClose={onClose}
        closable={false}
        styles={{
          body: { padding: 0 },
        }}
      >
        <div className={styles.paymentDrawer}>
          {/* 头部标题 */}
          <div className={styles.header}>
            <div className={styles.titleRow}>
              <span className={styles.title}>{t('pricing.payment.title')}</span>
              <CloseOutlined className={styles.closeIcon} onClick={onClose} />
            </div>
          </div>

          {/* 内容区域 */}
          <div className={styles.content}>
            {loading || !planData || !firstProduct ? (
              <div className={styles.loadingContainer}>
                <Spin size="large" tip={t('pricing.payment.loading')} />
              </div>
            ) : (
              <>
                {/* 扣款方式 */}
                <div className={styles.section}>
                  <div className={styles.sectionHeader}>
                    <span className={styles.sectionTitle}>
                      {t('pricing.payment.paymentMethod')}
                    </span>
                    <div className={styles.autoRenewControl}>
                      <span className={styles.autoRenewLabel}>
                        {t('pricing.payment.autoRenew')}
                      </span>
                      <div className={styles.switchWrapper}>
                        <Switch size="small" checked={autoRenew} onChange={setAutoRenew} />
                        <span className={styles.switchStatus}>
                          {autoRenew ? t('pricing.payment.on') : t('pricing.payment.off')}
                        </span>
                        <Tooltip
                          title={autoRenewTooltip}
                          placement="top"
                          classNames={{ root: 'auto-renew-tooltip' }}
                          styles={{ root: { width: 400, maxWidth: 420 } }}
                        >
                          <InfoCircleOutlined className={styles.infoIcon} />
                        </Tooltip>
                      </div>
                    </div>
                  </div>

                  <Radio.Group
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  >
                    <div className={styles.paymentMethod}>
                      <Radio value="bank_card" />
                      <div className={styles.paymentInfo}>
                        <div className={styles.paymentTitle}>{t('pricing.payment.bankCard')}</div>
                        <div className={styles.paymentDesc}>
                          <span>{t('pricing.payment.bankCardDesc.prefix')}</span>
                          <Bank1Icon className={styles.cardIcon} />
                          <Bank2Icon className={styles.cardIcon} />
                          <Bank3Icon className={styles.cardIcon} />
                          <span>{t('pricing.payment.bankCardDesc.suffix')}</span>
                        </div>
                      </div>
                    </div>
                  </Radio.Group>
                </div>

                {/* 订单详情 */}
                <div className={styles.section}>
                  <div className={styles.sectionTitle}>{t('pricing.payment.orderDetails')}</div>

                  <div className={styles.planName}>{planData?.plan?.planName}</div>

                  <div className={styles.detailsGrid}>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>
                        {t('pricing.payment.discountType')}
                      </span>
                      <span className={styles.detailValue}>
                        {planData?.plan?.discountRate != null && planData.plan.discountRate < 1
                          ? t('pricing.payment.discount', {
                              rate: (planData?.plan?.discountRate * 10).toFixed(1),
                            })
                          : t('pricing.payment.noDiscount')}
                      </span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>
                        {t('pricing.payment.originalPrice')}
                      </span>
                      <span className={styles.detailValue}>
                        {currencySymbol} {planData?.plan?.chargePrice.toFixed(2)}
                      </span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>
                        {t('pricing.payment.payableAmount')}
                      </span>
                      <span className={styles.detailValue}>
                        {currencySymbol} {planData?.plan?.actualPrice.toFixed(2)}
                        {(planData?.plan?.chargePrice || 0) > (planData?.plan?.actualPrice || 0) &&
                          `(${t('pricing.payment.discountedPrice')})`}
                      </span>
                    </div>
                  </div>

                  <div className={styles.detailsGrid}>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>{t('pricing.payment.productType')}</span>
                      <span className={styles.detailValue}>
                        {isZh ? planData?.plan?.planTypeCn : planData?.plan?.planType}
                      </span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>{t('pricing.payment.quantity')}</span>
                      <span className={styles.detailValue}>{firstProduct?.count}</span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>
                        {t('pricing.payment.quantityUnit')}
                      </span>
                      <span className={styles.detailValue}>
                        {isZh ? planData?.plan?.amountUnitCn : planData?.plan?.amountUnit}
                      </span>
                    </div>
                  </div>

                  <div className={styles.detailsGrid}>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>{t('pricing.payment.validPeriod')}</span>
                      <span className={styles.detailValue}>
                        {firstProduct?.productAmount}{' '}
                        {isZh
                          ? ({ day: '天', month: '月', year: '年' } as Record<string, string>)[
                              firstProduct?.productUnit ?? ''
                            ] ?? firstProduct?.productUnit
                          : firstProduct?.productUnit}
                      </span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>
                        {t('pricing.payment.effectiveTime')}
                      </span>
                      <Tooltip
                        title={t('pricing.payment.effectiveImmediately')}
                        placement="topLeft"
                      >
                        <span className={styles.detailValue}>
                          {t('pricing.payment.effectiveImmediately')}
                        </span>
                      </Tooltip>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>{t('pricing.payment.orderNumber')}</span>
                      <Tooltip title={planData?.orderOutNo} placement="topLeft">
                        <span className={styles.detailValue}>{planData?.orderOutNo}</span>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* 渐变分隔 */}
          <div className={styles.gradient} />

          {/* 协议同意 */}
          {planData && (
            <div className={styles.agreement}>
              <Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)}>
                <span className={styles.agreementText}>
                  {t('pricing.payment.agreement.prefix')}
                  <a
                    href="/policies/subscription-agreement"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.agreementLink}
                  >
                    {t('pricing.payment.agreement.subscriptionAgreement')}
                  </a>
                  {t('pricing.payment.agreement.and')}
                  <a
                    href="/policies/auto-renewal-agreement"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.agreementLink}
                  >
                    {t('pricing.payment.agreement.autoRenewalRules')}
                  </a>
                  {t('pricing.payment.agreement.suffix')}
                </span>
              </Checkbox>
            </div>
          )}

          {/* 底部支付栏 */}
          <div className={styles.footer}>
            {!loading && planData && firstProduct ? (
              <>
                <div className={styles.priceInfo}>
                  <span className={styles.priceLabel}>{t('pricing.payment.totalAmount')}</span>
                  <div className={styles.priceAmount}>
                    <span className={styles.price}>
                      {currencySymbol}
                      {planData.plan?.actualPrice.toFixed(2)}
                    </span>
                    {planData.orderOriginTotal > planData.orderTotal && (
                      <span className={styles.originalPriceStrike}>
                        {planData.plan?.chargePrice.toFixed(2)}
                      </span>
                    )}
                  </div>
                </div>
                <div className={styles.actions}>
                  <Button className={styles.cancelButton} onClick={onClose}>
                    {t('pricing.payment.cancel')}
                  </Button>
                  <Button
                    type="primary"
                    className={styles.payButton}
                    onClick={handlePay}
                    disabled={!agreed}
                  >
                    {t('pricing.payment.pay')}
                  </Button>
                </div>
              </>
            ) : (
              <div className={styles.actions}>
                <Button className={styles.cancelButton} onClick={onClose}>
                  {t('pricing.payment.cancel')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </Drawer>
    </ConfigProvider>
  );
};
